<template>
	<header :class="{ 'banner--transparent': !isLoggedIn }" class="banner">
		<div class="constrained">
			<div class="flex">
				<nuxt-link :to="basePath" class="banner__logo flex items-end">
          <div class="flex items-center">
            <img
              v-if="isFetching"
              class="mr-2"
              height="48"
              src="/img/gms-myprofile-blue-01.svg"
              width="48"
            />
            <avatar
              v-else-if="accountName"
              :size="48"
              :username="accountName"
              class="mr-2 flex-shrink-0"
            />
            <div class="text-grey-darker">
              <div class="text-xs leading-tight">{{ t('FOREST_OWNER_ACCOUNT') }}</div>
              <div class="text-xl leading-none">
                {{ isFetching ? t('LOADING') : accountName }}
              </div>
            </div>
          </div>
				</nuxt-link>
        <nuxt-link v-if="!isFetching && activeAccountData" :to="`/my-account/fo/${route.params.accountId}/certification-information`" class="banner__logo flex items-end">
          <div class="ml-2 mb-1">
            <GMBadge v-if="isPefcCertified" icon="badge-check">{{ t('PEFC_CERTIFIED') }}</GMBadge>
            <GMBadge color="blue" v-else-if="!isPefcCertified">{{ t('NOT_PEFC_CERTIFIED') }}</GMBadge>
          </div>
        </nuxt-link>
        <nuxt-link v-if="!isFetching && activeAccountData && isAllowedToBecomeShareholder" :to="`/my-account/fo/${route.params.accountId}/ShareholderInformation`" class="banner__logo flex items-end">
          <div class="ml-2 mb-1">
            <GMBadge v-if="isShareholder" icon="badge-check">{{ t('SHAREHOLDER') }}</GMBadge>
            <GMBadge color="blue" v-else-if="!isShareholder">{{ t('NOT_SHAREHOLDER') }}</GMBadge>
          </div>
        </nuxt-link>
				<menu-forest-owner/>
			</div>
		</div>
	</header>
</template>
<script>
import {GMAvatar as Avatar, GMBadge} from '@gm/components'
import {mapState} from "pinia";
import MenuForestOwner from '../MenuForestOwner.vue'
import {useUserStore} from "~/stores/user";
import {useDocumentsStore} from "~/stores/documents";
import {useAccountService} from "~/services/account";
import {ENVIRONMENTAL_AGREEMENT_TYPE_PEFC} from "~/constants/environmentalAgreement";
import {useUserService} from "~/services/user";
import {useRightOfUseService} from "~/services/rightOfUse";
import {getIsAllowedToBecomeShareholder} from "~/helpers/shareholder";
import {useAccountId} from "~/composables/useAccountId";
import {queryClient} from "~/plugins/vue-query";

export default {
	components: {
		Avatar,
    GMBadge,
		MenuForestOwner,
	},
  setup() {
    const { t } = useTranslation()
    const route = useRoute()
    const documentsStore = useDocumentsStore()
    const { accountsQuery, getBasePath, getAccountName, accountQuery, hasRole } = useAccountService();
    const { data: accounts, isFetching: isFetchingAccounts } = accountsQuery()
    const activeAccountId = useAccountId()
    const { data: activeAccountData, isFetching: isFetchingActiveAccount } = accountQuery(activeAccountId.value)

    const accountName = computed(() => getAccountName())
    const basePath = computed(() => getBasePath())

    const isFetching = computed(() => {
      return isFetchingAccounts.value || isFetchingActiveAccount.value
    })

    const { loggedInUserQuery } = useUserService()
    const { data: loggedInUser } = loggedInUserQuery()
    const { propertiesWithRightOfUseAccountQuery } = useRightOfUseService()
    const { data: propertiesWithRightOfUse } = propertiesWithRightOfUseAccountQuery({
      AccountIdCrm: activeAccountId.value,
      ContactIdCrm: loggedInUser.value,
      Name: activeAccountData.value?.Name,
      OrgNumber: activeAccountData.value?.OrganisationNumber
    })

    const isAllowedToBecomeShareholder = computed(() => {
      if (activeAccountData.value && propertiesWithRightOfUse.value && accounts.value) {
        return getIsAllowedToBecomeShareholder(activeAccountData.value, propertiesWithRightOfUse.value, accounts.value)
      }
      return false
    })

    const isPefcCertified = computed(() => {
      return activeAccountData.value && activeAccountData.value?.Certifications?.find(certification => certification.Type === ENVIRONMENTAL_AGREEMENT_TYPE_PEFC && certification.Certified === true)
    })

    watch(() => [activeAccountData.value, isPefcCertified.value], () => {
      if (activeAccountData.value && !isPefcCertified.value) {
        queryClient.refetchQueries({queryKey: ['accountGetAccount', activeAccountId.value]})
      }
    })

    return {
      t,
      route,
      documentsStore,
      isFetching,
      basePath,
      accountName,
      hasRole,
      activeAccountData,
      isAllowedToBecomeShareholder,
      isPefcCertified,
    }
  },
	computed: {
    isShareholder() {
      return this.hasRole('shareholder')
    },
		...mapState(useUserStore, ['isLoggedIn']),
	},
	watch: {
		account: {
			handler() {
				this.documentsStore.fetch()
			},
			immediate: true
		}
	},
}
</script>
